<template>
  <div class="publish-page">

    <!-- ═══════════════════ HERO ═══════════════════ -->
    <section class="publish-hero">
      <div class="container hero-content">
        <div class="row align-items-center g-5">
          <div class="col-lg-6">
            <div class="hero-badge mb-3">
              <i class="fa-solid fa-cloud-arrow-up me-2"></i>
              {{ $t('news.sources.upcommons') }}
            </div>
            <h1 class="hero-title" v-html="$t('publish.title')"></h1>
            <div class="hero-description mt-3">
              <p v-html="$t('publish.intro1')"></p>
              <p v-html="$t('publish.intro2')"></p>
            </div>
            <div class="mt-4">
              <a href="#howTo" class="btn btn-apren-primary px-4 py-2">
                {{ $t('publish.howToTitle') }}
              </a>
            </div>
          </div>
          <div class="col-lg-6 d-none d-lg-block text-center">
            <div class="hero-illustration">
              <i class="fa-solid fa-upload main-hero-icon"></i>
              <div class="floating-icons">
                <i class="fa-solid fa-file-pdf icon-f1"></i>
                <i class="fa-solid fa-video icon-f2"></i>
                <i class="fa-solid fa-file-lines icon-f3"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════ BENEFICIS ═══════════════════ -->
    <section class="container content-section" id="benefits">
      <div class="section-header">
        <div class="section-icon-wrap icon-blue">
          <i class="fa-solid fa-star"></i>
        </div>
        <div>
          <h2 class="section-title">{{ $t('publish.benefitsTitle') }}</h2>
          <p class="body-text mt-2" v-html="$t('publish.benefitsIntro')"></p>
        </div>
      </div>

      <div class="benefits-grid mt-4">
        <div class="benefit-card" v-for="i in 6" :key="i">
          <div class="benefit-icon-box">
            <i :class="getBenefitIcon(i)"></i>
          </div>
          <div class="benefit-content" v-html="getBenefitTranslation(i)"></div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════ ATENEA INTEGRATION — full-width blue gradient ═══════════════════ -->
    <section class="atenea-fullwidth" id="atenea">
      <div class="container">
        <div class="section-header">
          <div class="section-icon-wrap icon-light">
            <i class="fa-solid fa-link"></i>
          </div>
          <div>
            <h2 class="section-title section-title-light">{{ $t('publish.depositTitle') }}</h2>
          </div>
        </div>

        <div class="row g-5 mt-2">
          <div class="col-lg-7">
            <div class="atenea-info-card">
              <p class="body-text text-white mb-3" v-html="$t('publish.deposit1')"></p>
              <p class="body-text text-white mb-3" v-html="$t('publish.deposit2')"></p>
              <p class="body-text text-white mb-3" v-html="$t('publish.deposit3')"></p>
              <p class="body-text text-white" v-html="$t('publish.deposit4')"></p>
            </div>
          </div>
          <div class="col-lg-5">
            <div class="atenea-logo-box">
              <img src="../assets/images/logo-apren2.png" alt="Atenea" class="atenea-brand-img" />
              <div class="logo-connector">
                <i class="fa-solid fa-arrow-right-arrow-left"></i>
              </div>
              <div class="atenea-text-logo">ATENEA</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="container main-content mt-5" id="howTo">
      
      <!-- ═══════════════════ COM FER-HO (STEPS) ═══════════════════ -->
      <section class="content-section">
        <div class="section-header mb-5">
          <div class="section-icon-wrap icon-indigo">
            <i class="fa-solid fa-list-check"></i>
          </div>
          <div>
            <h2 class="section-title">{{ $t('publish.howToTitle') }}</h2>
            <p class="body-text mt-2" v-html="$t('publish.howTo1') + ' ' + $t('publish.howTo2')"></p>
          </div>
        </div>

        <!-- Timeline / Steps -->
        <div class="steps-container">
          <!-- Step 1 -->
          <div class="step-item">
            <div class="step-number">1</div>
            <div class="step-content">
              <div class="step-text" v-html="$t('publish.step1')"></div>
              <div class="step-image-wrap">
                <img src="../assets/images/publish/publish1.png" class="step-img" :alt="$t('publish.step1Alt')">
              </div>
            </div>
          </div>

          <!-- Step 2 -->
          <div class="step-item">
            <div class="step-number">2</div>
            <div class="step-content">
              <div class="step-text" v-html="$t('publish.step2')"></div>
              <div class="step-image-wrap">
                <img src="../assets/images/publish/publish2.png" class="step-img" :alt="$t('publish.step2Alt')">
              </div>
            </div>
          </div>

          <!-- Step 3 -->
          <div class="step-item">
            <div class="step-number">3</div>
            <div class="step-content">
              <div class="step-text" v-html="$t('publish.step3')"></div>
              <div class="step-image-wrap">
                <img src="../assets/images/publish/publish3.png" class="step-img" :alt="$t('publish.step3Alt')">
              </div>
            </div>
          </div>

          <!-- Step 4 -->
          <div class="step-item">
            <div class="step-number">4</div>
            <div class="step-content">
              <div class="step-text" v-html="$t('publish.step4')"></div>
              <div class="step-image-wrap">
                <img src="../assets/images/publish/publish4.png" class="step-img" :alt="$t('publish.step4Alt')">
              </div>
              <div class="step-notes mt-3">
                <div v-for="i in 3" :key="i" class="step-note-item">
                   <i class="fa-solid fa-circle-info me-2 text-apren-blue"></i>
                   <span v-html="getStep4NoteTranslation(i)"></span>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 5 -->
          <div class="step-item">
            <div class="step-number">5</div>
            <div class="step-content">
              <div class="step-text" v-html="$t('publish.step5')"></div>
              <div class="step-image-wrap">
                <img src="../assets/images/publish/publish5.png" class="step-img" :alt="$t('publish.step5Alt')">
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ═══════════════════ ESTATS — full-width grey gradient ═══════════════════ -->
      <section class="status-fullwidth my-5">
        <div class="container">
          <div class="section-header mb-4">
            <div class="section-icon-wrap icon-status">
              <i class="fa-solid fa-circle-notch"></i>
            </div>
            <div>
              <h2 class="section-title">{{ $t('publish.status.statusTitle') }}</h2>
              <p class="body-text mt-2" v-html="$t('publish.step5Intro')"></p>
            </div>
          </div>

          <div class="status-grid">
            <div class="status-card" v-for="state in ['pending', 'sent', 'review', 'rejected', 'published']" :key="state">
              <div class="status-icon-wrap" :class="'status-' + state">
                <i :class="getStatusIcon(state)"></i>
              </div>
              <div class="status-info">
                 <div class="status-label" v-html="getStatusLabel(state)"></div>
                 <div class="status-text" v-html="getStatusText(state)"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ═══════════════════ CONTACTE ═══════════════════ -->
      <section class="content-section pb-5" id="contact">
        <div class="section-header mb-4">
          <div class="section-icon-wrap icon-blue">
            <i class="fa-solid fa-comments"></i>
          </div>
          <div>
            <h2 class="section-title">{{ $t('publish.contactTitle') }}</h2>
            <p class="body-text mt-2" v-html="$t('publish.contactIntro')"></p>
          </div>
        </div>

        <div class="libraries-grid mt-4">
          <div class="library-card" v-for="(library, key) in libraries" :key="key">
            <div class="library-icon">
              <i class="fa-solid fa-building-columns"></i>
            </div>
            <div class="library-content" v-html="getLibraryTranslation(key)"></div>
          </div>
        </div>
      </section>

    </div>
  </div>
</template>

<script>
import { onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import store from '@/store'

export default {
  name: 'PublishView',
  setup() {
    const route = useRoute()
    const { t } = useI18n()
    
    // Lista de bibliotecas para iterar
    const libraries = {
      brgf: 'brgf',
      bct: 'bct',
      bcbl: 'bcbl',
      bcum: 'bcum',
      etseib: 'etseib',
      etsab: 'etsab',
      eebe: 'eebe',
      epseb: 'epseb',
      epsevg: 'epsevg',
      etsav: 'etsav',
      fme: 'fme',
      fnb: 'fnb'
    }

    const getBenefitIcon = (index) => {
      const icons = [
        'fa-solid fa-id-card-clip',
        'fa-solid fa-shield-halved',
        'fa-solid fa-creative-commons',
        'fa-solid fa-bullhorn',
        'fa-solid fa-chart-line',
        'fa-solid fa-link'
      ]
      return icons[index - 1] || 'fa-solid fa-check-circle'
    }

    const getStatusIcon = (state) => {
       const icons = {
         pending: 'fa-solid fa-clock',
         sent: 'fa-solid fa-paper-plane',
         review: 'fa-solid fa-magnifying-glass',
         rejected: 'fa-solid fa-circle-xmark',
         published: 'fa-solid fa-circle-check'
       }
       return icons[state]
    }

    const getStatusLabel = (state) => {
       const html = t(`publish.status.${state}`)
       // Separamos el strong del resto
       const parts = html.split(':')
       return parts[0] || state
    }

    const getStatusText = (state) => {
       const html = t(`publish.status.${state}`)
       const parts = html.split(':')
       return parts[1] || ''
    }

    // Helper functions for dynamic translations
    const getLibraryTranslation = (key) => {
      try {
        return t(`publish.libraries.${key}`)
      } catch (e) {
        return ''
      }
    }

    const getBenefitTranslation = (index) => {
      try {
        return t(`publish.benefits.${index}`)
      } catch (e) {
        return ''
      }
    }

    const getStep4NoteTranslation = (index) => {
      try {
        return t(`publish.step4Notes.${index}`)
      } catch (e) {
        return ''
      }
    }

    onMounted(async () => {
      await store.dispatch('updateSearchSelected', "Tots");
      await store.dispatch('updateBackgroundImage', { 
        view: route.name, 
        url: require('@/assets/images/sections/banner-blue.webp') 
      });
    });

    return {
      libraries,
      getLibraryTranslation,
      getBenefitTranslation,
      getStep4NoteTranslation,
      getBenefitIcon,
      getStatusIcon,
      getStatusLabel,
      getStatusText
    }
  }
}
</script>

<style scoped>
/* ════════════════════════════════════════════════
   GENERAL & VARIABLES (Replicando estilo de News)
════════════════════════════════════════════════ */
.publish-page {
  --apren-500: #007BC1;
  --apren-400: #0093D8;
  --text-primary: #0c2340;
  --text-secondary: #4a5568;
  --text-muted: #718096;
  --border: #e2e8f0;
  --bg-light: #f8fafc;
  
  font-family: 'Roboto', Helvetica, serif;
  color: var(--text-primary);
  background: white;
  margin-top: -1.5rem; /* Ajustar por el layout */
}

.content-section {
  padding: 5rem 0;
}

.body-text {
  font-size: 18px;
  font-weight: 300;
  line-height: 28px;
  color: var(--text-secondary);
}

/* ════════════════════════════════════════════════
   SECTION HEADERS
════════════════════════════════════════════════ */
.section-header {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  margin-bottom: 2.5rem;
}

.section-icon-wrap {
  flex-shrink: 0;
  width: 54px;
  height: 54px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}

.icon-blue { background: #e0f2fe; color: var(--apren-500); }
.icon-indigo { background: #e0e7ff; color: #4f46e5; }
.icon-status { background: #fef3c7; color: #d97706; }
.icon-light { background: rgba(255,255,255,0.2); color: white; border: 1px solid rgba(255,255,255,0.3); }

.section-title {
  font-size: 2.25rem;
  font-weight: 800;
  color: var(--text-primary);
  margin: 0;
  letter-spacing: -0.02em;
}

.section-title-light {
  color: white;
}

/* ════════════════════════════════════════════════
   HERO
════════════════════════════════════════════════ */
.publish-hero {
  background: linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%);
  padding: 6rem 0;
  position: relative;
  overflow: hidden;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid var(--border);
  border-radius: 100px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--apren-500);
  box-shadow: 0 2px 4px rgba(0,0,0,0.03);
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 900;
  line-height: 1.1;
  color: var(--text-primary);
  letter-spacing: -0.03em;
}

.hero-description {
  font-size: 1.2rem;
  line-height: 1.7;
  color: var(--text-secondary);
  font-weight: 300;
}

.hero-illustration {
  position: relative;
  width: 300px;
  height: 300px;
  margin: 0 auto;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 20px 50px rgba(0,0,0,0.08);
}

.main-hero-icon {
  font-size: 100px;
  color: var(--apren-500);
}

.floating-icons i {
  position: absolute;
  width: 60px;
  height: 60px;
  background: var(--apren-400);
  color: white;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  box-shadow: 0 10px 20px rgba(0, 147, 216, 0.3);
  animation: float 4s ease-in-out infinite;
}

.icon-f1 { top: -10px; right: -10px; animation-delay: 0s; }
.icon-f2 { bottom: 20px; left: -30px; animation-delay: 1s; }
.icon-f3 { bottom: -20px; right: 40px; animation-delay: 2s; }

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-15px); }
}

/* ════════════════════════════════════════════════
   BENEFITS GRID
════════════════════════════════════════════════ */
.benefits-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.benefit-card {
  background: white;
  padding: 2rem;
  border-radius: 20px;
  border: 1px solid var(--border);
  transition: all 0.3s ease;
  height: 100%;
}

.benefit-card:hover {
  transform: translateY(-8px);
  border-color: var(--apren-400);
  box-shadow: 0 15px 40px rgba(0, 123, 193, 0.1);
}

.benefit-icon-box {
  width: 50px;
  height: 50px;
  background: #f0f9ff;
  color: var(--apren-500);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  margin-bottom: 1.25rem;
}

.benefit-content {
  font-size: 18px;
  font-weight: 300;
  line-height: 1.5;
  color: var(--text-secondary);
}

/* ════════════════════════════════════════════════
   ATENEA FULL WIDTH (Blue Gradient)
════════════════════════════════════════════════ */
.atenea-fullwidth {
  background: linear-gradient(135deg, #007BC1 0%, #005a8e 100%);
  padding: 5rem 0;
  margin-left: calc(-50vw + 50%);
  margin-right: calc(-50vw + 50%);
  padding-left: calc(50vw - 50%);
  padding-right: calc(50vw - 50%);
  color: white;
}

.atenea-info-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
  padding: 2.5rem;
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.atenea-logo-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 1.5rem;
}

.atenea-brand-img {
  width: 140px;
  filter: brightness(0) invert(1);
}

.logo-connector {
  font-size: 2rem;
  color: rgba(255,255,255,0.5);
}

.atenea-text-logo {
  font-size: 3rem;
  font-weight: 900;
  letter-spacing: 0.1em;
  color: white;
}

/* ════════════════════════════════════════════════
   STEPS (TIMELINE)
════════════════════════════════════════════════ */
.steps-container {
  display: flex;
  flex-direction: column;
  gap: 4rem;
  position: relative;
}

.steps-container::before {
  content: '';
  position: absolute;
  left: 20px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: var(--border);
  z-index: 0;
}

.step-item {
  display: flex;
  gap: 2.5rem;
  position: relative;
  z-index: 1;
}

.step-number {
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  background: var(--apren-500);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 1.25rem;
  box-shadow: 0 0 0 8px white;
}

.step-content {
  flex-grow: 1;
}

.step-text {
  font-size: 22px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 1.5rem;
}

.step-image-wrap {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid var(--border);
  box-shadow: 0 10px 30px rgba(0,0,0,0.05);
  background: white;
  padding: 1rem;
}

.step-img {
  width: 100%;
  height: auto;
  border-radius: 8px;
}

.step-notes {
  background: var(--bg-light);
  padding: 1.5rem;
  border-radius: 12px;
  border-left: 4px solid var(--apren-500);
}

.step-note-item {
  font-size: 16px;
  font-weight: 300;
  line-height: 24px;
  margin-bottom: 0.75rem;
  color: var(--text-secondary);
}

/* ════════════════════════════════════════════════
   STATUS GRID (Grey Gradient Strip)
════════════════════════════════════════════════ */
.status-fullwidth {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 4rem 0;
  margin-left: calc(-50vw + 50%);
  margin-right: calc(-50vw + 50%);
  padding-left: calc(50vw - 50%);
  padding-right: calc(50vw - 50%);
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1.5rem;
}

.status-card {
  background: white;
  padding: 1.5rem;
  border-radius: 16px;
  border: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.status-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.status-pending { background: #e2e8f0; color: #64748b; }
.status-sent { background: #dcfce7; color: #166534; }
.status-review { background: #dbeafe; color: #1e40af; }
.status-rejected { background: #fee2e2; color: #991b1b; }
.status-published { background: #f0f9ff; color: #0369a1; }

.status-label {
  font-weight: 700;
  font-size: 1.1rem;
  color: var(--text-primary);
}

.status-text {
  font-size: 14px;
  font-weight: 300;
  line-height: 1.4;
  color: var(--text-secondary);
}

/* ════════════════════════════════════════════════
   LIBRARIES GRID
════════════════════════════════════════════════ */
.libraries-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.25rem;
}

.library-card {
  background: white;
  padding: 1.25rem;
  border-radius: 12px;
  border: 1px solid var(--border);
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.2s ease;
}

.library-card:hover {
  border-color: var(--apren-400);
  background: var(--bg-light);
}

.library-icon {
  color: var(--apren-500);
  font-size: 1.1rem;
}

.library-content {
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
}

/* Botón personalizado */
.btn-apren-primary {
  background: var(--apren-500);
  color: white;
  border-radius: 10px;
  font-weight: 600;
  transition: all 0.2s;
  border: none;
}

.btn-apren-primary:hover {
  background: var(--apren-400);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 123, 193, 0.2);
}

@media (max-width: 992px) {
  .benefits-grid { grid-template-columns: repeat(2, 1fr); }
  .status-grid { grid-template-columns: repeat(2, 1fr); }
  .libraries-grid { grid-template-columns: repeat(2, 1fr); }
  .hero-title { font-size: 2.5rem; }
}

@media (max-width: 576px) {
  .benefits-grid { grid-template-columns: 1fr; }
  .status-grid { grid-template-columns: 1fr; }
  .libraries-grid { grid-template-columns: 1fr; }
}
</style>

